import { useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';

const ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 min
const BROADCAST_THROTTLE = 30 * 1000;
const ACTIVITY_EVENTS = ['keydown', 'scroll', 'touchstart', 'click'];

export function KeycloakTokenManager() {
  const { keycloak } = useKeycloak();
  const lastActivityRef = useRef(Date.now());
  const lastBroadcastRef = useRef(Date.now());
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!keycloak?.authenticated || !keycloak.tokenParsed?.exp) {
      return;
    }

    const activityChannel = new BroadcastChannel('keycloak-activity');
    channelRef.current = activityChannel;

    const markActive = () => {
      const now = Date.now();
      lastActivityRef.current = now;

      if (document.visibilityState !== 'visible') return;

      if (now - lastBroadcastRef.current > BROADCAST_THROTTLE) {
        lastBroadcastRef.current = now;
        activityChannel.postMessage({ type: 'user-active', timestamp: now });
      }
    };

    activityChannel.onmessage = (event) => {
      if (event.data?.type === 'user-active') {
        lastActivityRef.current = event.data.timestamp;
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActive)
    );

    const scheduleTokenRefresh = () => {
      const now = Math.floor(Date.now() / 1000);
      const expiresIn = keycloak.tokenParsed!.exp - now;
      const refreshIn = (expiresIn - 70) * 1000;

      if (refreshIn <= 0) {
        return;
      }

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        const isActive = Date.now() - lastActivityRef.current < ACTIVITY_TIMEOUT;

        if (isActive && keycloak.token) {
          keycloak.updateToken(70).then((refreshed: boolean) => {
            scheduleTokenRefresh();
          }).catch(() => {
            keycloak.logout();
          });
        } else {
          keycloak.logout();
        }
      }, refreshIn);
    };

    scheduleTokenRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      activityChannel.close();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActive)
      );
    };
  }, [
    keycloak.authenticated,
    keycloak.tokenParsed?.exp,
  ]);

  return null;
}
